import type { ReferencedValue, ValueTombstone } from "../schemas/shared/valueConfig.ts";
import { isReferencedValue, referencedNodeValue } from "../schemas/shared/valueConfig.ts";

const lockedRefToDoomedNode = (candidate: unknown, isDoomed: (nodeId: number) => boolean) => {
  const reference = referencedNodeValue(candidate);
  return reference?.locked && isDoomed(reference.nodeId) ? (candidate as ReferencedValue) : undefined;
};

/**
 * Every locked node-value reference within `config` whose node is about to go —
 * the references a removal tombstones rather than clears, since each is the type
 * authority for its slot.
 *
 * Asked before anything is written, so a signature that cannot be resolved can
 * reject the removal while the config is still untouched. Walks the whole
 * structure for the same reason `resetNodeValueRefs` does: configured values sit
 * under arbitrary keys.
 */
export const findLockedNodeValueRefs = (config: unknown, isDoomed: (nodeId: number) => boolean): ReferencedValue[] => {
  const found: ReferencedValue[] = [];

  const walk = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node !== "object" || node === null) return;

    const locked = lockedRefToDoomedNode(node, isDoomed);
    if (locked) {
      found.push(locked);
      return;
    }
    Object.values(node).forEach(walk);
  };

  walk(config);
  return found;
};

/**
 * A deep copy of `config` with every reference `toTombstone` answers for replaced
 * by that tombstone, so the slot keeps the signature of the value it was bound to
 * instead of being cleared. References it declines are left as they stand.
 *
 * Immutable, like `repointNodeValueRefs`: the result is dispatched as the config
 * to store, not written into the state being read.
 */
export const tombstoneLockedValueRefs = <T>(
  config: T,
  toTombstone: (reference: ReferencedValue) => ValueTombstone | undefined,
): T => {
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (typeof node !== "object" || node === null) return node;

    if (isReferencedValue(node)) return toTombstone(node) ?? node;

    return Object.fromEntries(Object.entries(node).map(([key, child]) => [key, walk(child)]));
  };

  return walk(config) as T;
};
